import { createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import { apiGateway } from '../../../../config';
import { FACTORIES, MONTHS } from '../../../../constants';

import type { AxiosResponse } from 'axios';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { AppThunk } from '../../../../redux/store';
import type { ErrorResponse } from '../../../../@types/common';
import type { ShippingSchedule, Factory, Item, Port } from '../../../../@types/common';
import type { ScheduleFactory, Schedule, ScheduleGridData, ScheduleSet, ScheduleState } from '../../../../@types/schedule';
import type { DeliveryMessage } from '../../../../@types/deliveryMessage';

// 2021〜来年までの年を取得
const YEARS: string[] = ((): string[] => {
  let years = [];
  const nextYear = new Date().getFullYear() + 1;
  for (let i = nextYear; i >= 2021; i--) {
    years.push(String(i));
  }
  return years;
})();

// ----------------------------------------------------------------------

const initialState: ScheduleState = {
  isLoading: false,
  hasError: false,
  message: undefined,
  isModalOpen: false,
  deliveryMessage: undefined,
  options: {
    factory: undefined,
    item: undefined,
    port: undefined,
    year: YEARS,
    month: MONTHS,
    shippingSchedule: undefined,
  },
  schedulesKashima: [],
  schedulesKashimaPrev: [],
  schedulesKobe: [],
  schedulesKobePrev: [],
  schedulesFunabashi: [],
  schedulesFunabashiPrev: [],
  selectedYear: new Date().getFullYear().toString(),
  selectedMonth: (0 + (new Date().getMonth() + 1).toString()).slice(-2),
  selectedFactory: FACTORIES.KOBE,
  selectedShippingSchedule: undefined,
};

const slice = createSlice({
  name: 'schedule',
  initialState,
  reducers: {
    startLoading(state: ScheduleState) {
      state.isLoading = true;
    },

    hasError(state: ScheduleState, action: PayloadAction<string>) {
      state.isLoading = false;
      state.hasError = true;
      state.message = action.payload;
    },

    resetMessage(state: ScheduleState) {
      state.isLoading = false;
      state.hasError = false;
      state.message = '';
    },

    openModal(state: ScheduleState, action: PayloadAction<boolean>) {
      state.isModalOpen = action.payload;
    },

    // ----------------------------------------------------------------------

    changeScheduleYear(state: ScheduleState, action: PayloadAction<string>) {
      state.selectedYear = action.payload;
    },

    changeScheduleMonth(state: ScheduleState, action: PayloadAction<string>) {
      state.selectedMonth = action.payload;
    },

    changeScheduleFactory(state: ScheduleState, action: PayloadAction<ScheduleFactory>) {
      state.selectedFactory = action.payload;
    },

    changeShippingSchedule(state: ScheduleState, action: PayloadAction<ShippingSchedule>) {
      state.selectedShippingSchedule = action.payload;
    },

    // ----------------------------------------------------------------------

    getOptionsFactorySuccess(state: ScheduleState, action: PayloadAction<Factory[]>) {
      state.isLoading = false;
      state.options.factory = action.payload;
    },

    getOptionsItemSuccess(state: ScheduleState, action: PayloadAction<Item[]>) {
      state.isLoading = false;
      state.options.item = action.payload;
    },

    getOptionsPortSuccess(state: ScheduleState, action: PayloadAction<Port[]>) {
      state.isLoading = false;
      state.options.port = action.payload;
    },

    getOptionsShippingScheduleSuccess(state: ScheduleState, action: PayloadAction<ShippingSchedule[]>) {
      state.isLoading = false;
      state.options.shippingSchedule = action.payload;
    },

    // ----------------------------------------------------------------------

    getDeliveryMessageSuccess(state: ScheduleState, action: PayloadAction<DeliveryMessage>) {
      state.isLoading = false;
      state.hasError = false;
      state.message = undefined;
      state.deliveryMessage = action.payload.message;
    },

    getSchedulesSuccess(state: ScheduleState, action: PayloadAction<ScheduleSet>) {
      // console.log('getSchedulesSuccess', state.selectedFactory);
      state.isLoading = false;
      state.hasError = false;
      state.message = undefined;
      // 対応する工場のスケジュール（Current）
      const schedules: Schedule[] = action.payload.deliveryChart;
      const schedulesGridData: ScheduleGridData[] = schedules.map((schedule: Schedule) => {
        const keyId: string =
          state.selectedFactory +
          '-' +
          schedule.makerDeliveryDate +
          '-' +
          schedule.shippingScheduleId +
          '-' +
          schedule.containerNumber +
          '-' +
          (schedule.isRokko ? 'true' : 'false');
        return Object.assign({}, { keyId }, schedule, schedule.items);
        // return Object.assign({}, { keyId }, schedule, schedule.items, {comment: schedule.comment.join(', ')});
      });
      // console.log(schedulesGridData);
      if (state.selectedFactory === FACTORIES.KOBE) {
        state.schedulesKobe = schedulesGridData;
      } else if (state.selectedFactory === FACTORIES.KASHIMA) {
        state.schedulesKashima = schedulesGridData;
      } else if (state.selectedFactory === FACTORIES.FUNABASHI) {
        state.schedulesFunabashi = schedulesGridData;
      }
      // 対応する工場のスケジュール（Previous）
      const schedulesPrev: Schedule[] = action.payload.deliveryChartPrev;
      const schedulesPrevGridData: ScheduleGridData[] = schedulesPrev.map((schedule: Schedule) => {
        const keyId: string =
          state.selectedFactory + '-' + schedule.makerDeliveryDate + '-' + schedule.shippingScheduleId + '-' + schedule.containerNumber;
        return Object.assign({}, { keyId }, schedule, schedule.items, { comment: schedule.comment.join(', ') });
      });
      // console.log(schedulesPrevGridData);
      if (state.selectedFactory === FACTORIES.KOBE) {
        state.schedulesKobePrev = schedulesPrevGridData;
      } else if (state.selectedFactory === FACTORIES.KASHIMA) {
        state.schedulesKashimaPrev = schedulesPrevGridData;
      } else if (state.selectedFactory === FACTORIES.FUNABASHI) {
        state.schedulesFunabashiPrev = schedulesPrevGridData;
      }
    },

    changeScheduleShippedSuccess(state: ScheduleState, action: PayloadAction<ShippingSchedule>) {
      state.isLoading = false;
      state.hasError = false;
      state.message = 'successfully updated';
      // 各工場の出港状況を更新
      const changedShippingSchedule = action.payload;
      state.schedulesKashima = state.schedulesKashima
        ? state.schedulesKashima.map((schedule: ScheduleGridData) => {
            if (schedule.shippingScheduleId === changedShippingSchedule.id) {
              return Object.assign({}, schedule, { isShipped: changedShippingSchedule.isShipped });
            } else {
              return schedule;
            }
          })
        : state.schedulesKashima;
      state.schedulesKobe = state.schedulesKobe
        ? state.schedulesKobe.map((schedule: ScheduleGridData) => {
            if (schedule.shippingScheduleId === changedShippingSchedule.id) {
              return Object.assign({}, schedule, { isShipped: changedShippingSchedule.isShipped });
            } else {
              return schedule;
            }
          })
        : state.schedulesKobe;
      state.schedulesFunabashi = state.schedulesFunabashi
        ? state.schedulesFunabashi.map((schedule: ScheduleGridData) => {
            if (schedule.shippingScheduleId === changedShippingSchedule.id) {
              return Object.assign({}, schedule, { isShipped: changedShippingSchedule.isShipped });
            } else {
              return schedule;
            }
          })
        : state.schedulesFunabashi;
      // options.shippingSchedule を更新
      state.options.shippingSchedule = state.options.shippingSchedule
        ? state.options.shippingSchedule.map((schedule: ShippingSchedule) => {
            if (schedule.id === changedShippingSchedule.id) {
              return changedShippingSchedule;
            } else {
              return schedule;
            }
          })
        : state.options.shippingSchedule;
      // 選択中のスケジュールを破棄
      state.selectedShippingSchedule = undefined;
    },
  },
});

export default slice.reducer;

// TODO: https://stackoverflow.com/questions/63439021/handling-errors-with-redux-toolkit

// ----------------------------------------------------------------------

export function openModal(open: boolean): AppThunk {
  return (dispatch): void => {
    dispatch(slice.actions.openModal(open));
  };
}

export function resetMessage(): AppThunk {
  return (dispatch): void => {
    dispatch(slice.actions.resetMessage());
  };
}

// ----------------------------------------------------------------------

export function changeScheduleYear(year: string): AppThunk {
  return (dispatch): void => {
    dispatch(slice.actions.changeScheduleYear(year));
  };
}

export function changeScheduleMonth(month: string): AppThunk {
  return (dispatch): void => {
    dispatch(slice.actions.changeScheduleMonth(month));
  };
}

export function changeScheduleFactory(factory: ScheduleFactory): AppThunk {
  return (dispatch): void => {
    dispatch(slice.actions.changeScheduleFactory(factory));
  };
}

export function changeShippingSchedule(shippingSchedule: ShippingSchedule): AppThunk {
  return (dispatch): void => {
    dispatch(slice.actions.changeShippingSchedule(shippingSchedule));
  };
}

// ----------------------------------------------------------------------

export function getOptionsFactory(): AppThunk {
  return async (dispatch): Promise<void> => {
    dispatch(slice.actions.startLoading());
    try {
      const token = (await fetchAuthSession()).tokens?.idToken?.toString();
      const response: AxiosResponse = await axios.get<Factory[]>(apiGateway + '/options/factory', {
        headers: {
          Authorization: 'Bearer ' + token,
        },
        timeout: 60000,
      });
      dispatch(slice.actions.getOptionsFactorySuccess(response.data));
    } catch (error) {
      if (axios.isAxiosError(error)) {
        // error: AxiosError
        if (error.response) {
          const serverResponse: ErrorResponse = error.response.data;
          dispatch(slice.actions.hasError(serverResponse.message));
        } else {
          dispatch(slice.actions.hasError(error.message));
        }
      } else {
        if (error instanceof Error) {
          // error: Error
          dispatch(slice.actions.hasError(error.message));
        } else {
          const message = 'Unknown error';
          dispatch(slice.actions.hasError(message));
        }
      }
    }
  };
}

export function getOptionsItem(): AppThunk {
  return async (dispatch): Promise<void> => {
    dispatch(slice.actions.startLoading());
    try {
      const token = (await fetchAuthSession()).tokens?.idToken?.toString();
      const response: AxiosResponse = await axios.get<Item[]>(apiGateway + '/options/item', {
        headers: {
          Authorization: 'Bearer ' + token,
        },
        timeout: 60000,
      });
      dispatch(slice.actions.getOptionsItemSuccess(response.data));
    } catch (error) {
      if (axios.isAxiosError(error)) {
        // error: AxiosError
        if (error.response) {
          const serverResponse: ErrorResponse = error.response.data;
          dispatch(slice.actions.hasError(serverResponse.message));
        } else {
          dispatch(slice.actions.hasError(error.message));
        }
      } else {
        if (error instanceof Error) {
          // error: Error
          dispatch(slice.actions.hasError(error.message));
        } else {
          const message = 'Unknown error';
          dispatch(slice.actions.hasError(message));
        }
      }
    }
  };
}

export function getOptionsPort(): AppThunk {
  return async (dispatch): Promise<void> => {
    dispatch(slice.actions.startLoading());
    try {
      const token = (await fetchAuthSession()).tokens?.idToken?.toString();
      const response: AxiosResponse = await axios.get<Port[]>(apiGateway + '/options/port', {
        headers: {
          Authorization: 'Bearer ' + token,
        },
        timeout: 60000,
      });
      dispatch(slice.actions.getOptionsPortSuccess(response.data));
    } catch (error) {
      if (axios.isAxiosError(error)) {
        // error: AxiosError
        if (error.response) {
          const serverResponse: ErrorResponse = error.response.data;
          dispatch(slice.actions.hasError(serverResponse.message));
        } else {
          dispatch(slice.actions.hasError(error.message));
        }
      } else {
        if (error instanceof Error) {
          // error: Error
          dispatch(slice.actions.hasError(error.message));
        } else {
          const message = 'Unknown error';
          dispatch(slice.actions.hasError(message));
        }
      }
    }
  };
}

export function getOptionsShippingSchedule(ids?: number[]): AppThunk {
  return async (dispatch): Promise<void> => {
    dispatch(slice.actions.startLoading());
    try {
      const token = (await fetchAuthSession()).tokens?.idToken?.toString();
      const params = ids
        ? {
            where: {
              id: {
                in: ids,
              },
            },
          }
        : {};
      const response: AxiosResponse = await axios.get<ShippingSchedule[]>(apiGateway + '/shipping', {
        headers: {
          Authorization: 'Bearer ' + token,
        },
        params: {
          filter: JSON.stringify(params),
        },
        timeout: 60000,
      });
      // console.log(response.data);
      dispatch(slice.actions.getOptionsShippingScheduleSuccess(response.data));
    } catch (error) {
      if (axios.isAxiosError(error)) {
        // error: AxiosError
        if (error.response) {
          const serverResponse: ErrorResponse = error.response.data;
          dispatch(slice.actions.hasError(serverResponse.message));
        } else {
          dispatch(slice.actions.hasError(error.message));
        }
      } else {
        if (error instanceof Error) {
          // error: Error
          dispatch(slice.actions.hasError(error.message));
        } else {
          const message = 'Unknown error';
          dispatch(slice.actions.hasError(message));
        }
      }
    }
  };
}

// ----------------------------------------------------------------------

export function getDeliveryMessage(): AppThunk {
  return async (dispatch): Promise<void> => {
    dispatch(slice.actions.startLoading());
    try {
      const token = (await fetchAuthSession()).tokens?.idToken?.toString();
      const response: AxiosResponse = await axios.get<DeliveryMessage>(apiGateway + '/deliveryChart/deliveryMessage', {
        headers: {
          Authorization: 'Bearer ' + token,
        },
        timeout: 60000,
      });
      dispatch(slice.actions.getDeliveryMessageSuccess(response.data));
    } catch (error) {
      if (axios.isAxiosError(error)) {
        // error: AxiosError
        if (error.response) {
          const serverResponse: ErrorResponse = error.response.data;
          dispatch(slice.actions.hasError(serverResponse.message));
        } else {
          dispatch(slice.actions.hasError(error.message));
        }
      } else {
        if (error instanceof Error) {
          // error: Error
          dispatch(slice.actions.hasError(error.message));
        } else {
          const message = 'Unknown error';
          dispatch(slice.actions.hasError(message));
        }
      }
    }
  };
}

export function getSchedules(): AppThunk {
  return async (dispatch, getState): Promise<void> => {
    dispatch(slice.actions.startLoading());
    try {
      const token = (await fetchAuthSession()).tokens?.idToken?.toString();
      const year: string = getState().schedule.selectedYear || '';
      const month: string = getState().schedule.selectedMonth || '';
      const date: string = year + '-' + month;
      const factory: ScheduleFactory = getState().schedule.selectedFactory;
      const response: AxiosResponse = await axios.get<ScheduleSet>(apiGateway + '/deliveryChart/search', {
        headers: {
          Authorization: 'Bearer ' + token,
        },
        params: {
          date,
          factory,
        },
        timeout: 60000,
      });
      dispatch(slice.actions.getSchedulesSuccess(response.data));
    } catch (error) {
      if (axios.isAxiosError(error)) {
        // error: AxiosError
        if (error.response) {
          const serverResponse: ErrorResponse = error.response.data;
          dispatch(slice.actions.hasError(serverResponse.message));
        } else {
          dispatch(slice.actions.hasError(error.message));
        }
      } else {
        if (error instanceof Error) {
          // error: Error
          dispatch(slice.actions.hasError(error.message));
        } else {
          const message = 'Unknown error';
          dispatch(slice.actions.hasError(message));
        }
      }
    }
    // setTimeout(() => {
    //   dispatch(slice.actions.getSchedulesSuccess([scheduleData[0]]));
    // } , 2000);
  };
}

// ----------------------------------------------------------------------

export function changeScheduleShipped(id: number, shipped: boolean): AppThunk {
  return async (dispatch): Promise<void> => {
    dispatch(slice.actions.startLoading());
    try {
      const token = (await fetchAuthSession()).tokens?.idToken?.toString();
      const endpoint: string = shipped === true ? '/deliveryChart/doDelivery/' : '/deliveryChart/cancelDelivery/';
      // リクエスト
      const response: AxiosResponse = await axios.put<ShippingSchedule>(
        apiGateway + endpoint + id,
        {},
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
          timeout: 60000,
        },
      );
      dispatch(slice.actions.changeScheduleShippedSuccess(response.data));
    } catch (error) {
      if (axios.isAxiosError(error)) {
        // error: AxiosError
        if (error.response) {
          const serverResponse: ErrorResponse = error.response.data;
          dispatch(slice.actions.hasError(serverResponse.message));
        } else {
          dispatch(slice.actions.hasError(error.message));
        }
      } else {
        if (error instanceof Error) {
          // error: Error
          dispatch(slice.actions.hasError(error.message));
        } else {
          const message = 'Unknown error';
          dispatch(slice.actions.hasError(message));
        }
      }
    }
  };
}

// ----------------------------------------------------------------------
